import React, { useEffect, useState } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import workAssignmentService, {
  WorkCategoryCode,
  AdminShiftStatus,
  ShiftWindow,
  STATUS_LABELS,
  CATEGORY_LABELS,
  DAYS_OF_WEEK_FA,
  formatMinuteFa,
} from '../../services/workAssignment.service';

interface Props {
  category: WorkCategoryCode;
}

type Variant = 'success' | 'warning' | 'error';

const VARIANT_STYLES: Record<Variant, {
  bg: string;
  border: string;
  iconBg: string;
  iconColor: string;
  title: string;
  text: string;
  chipBg: string;
  chipText: string;
}> = {
  success: {
    bg: 'rgba(34, 197, 94, 0.08)',
    border: 'rgba(34, 197, 94, 0.25)',
    iconBg: '#dcfce7',
    iconColor: '#16a34a',
    title: '#15803d',
    text: '#166534',
    chipBg: '#16a34a',
    chipText: '#fff',
  },
  warning: {
    bg: 'rgba(245, 158, 11, 0.08)',
    border: 'rgba(245, 158, 11, 0.25)',
    iconBg: '#fef3c7',
    iconColor: '#d97706',
    title: '#92400e',
    text: '#78350f',
    chipBg: '#d97706',
    chipText: '#fff',
  },
  error: {
    bg: 'rgba(220, 38, 38, 0.08)',
    border: 'rgba(220, 38, 38, 0.25)',
    iconBg: '#fee2e2',
    iconColor: '#dc2626',
    title: '#991b1b',
    text: '#7f1d1d',
    chipBg: '#dc2626',
    chipText: '#fff',
  },
};

const MyShiftStatusBanner: React.FC<Props> = ({ category }) => {
  const [status, setStatus] = useState<AdminShiftStatus | null>(null);
  const [hasCategory, setHasCategory] = useState<boolean | null>(null);
  const [shiftWindow, setShiftWindow] = useState<ShiftWindow>({});

  useEffect(() => {
    let alive = true;
    workAssignmentService
      .getMyStatus()
      .then((res) => {
        if (!alive) return;
        setStatus(res.liveStatus);
        const cats = res.profile?.categories || [];
        setHasCategory(cats.some((c: any) => c.code === category));
        setShiftWindow(res.shiftWindow || {});
      })
      .catch(() => {
        if (alive) {
          setStatus(null);
          setHasCategory(null);
          setShiftWindow({});
        }
      });
    return () => {
      alive = false;
    };
  }, [category]);

  if (status === null || hasCategory === null) return null;
  if (status === 'no_profile') return null;

  const info = STATUS_LABELS[status] ?? STATUS_LABELS.off_shift;
  const isEligible = hasCategory && status === 'on_shift';
  const variant: Variant = !hasCategory ? 'error' : isEligible ? 'success' : 'warning';
  const s = VARIANT_STYLES[variant];

  const Icon =
    variant === 'success' ? CheckCircleOutlineIcon :
    variant === 'warning' ? WarningAmberIcon : ErrorOutlineIcon;

  // Build title + subtitle
  let title = '';
  let subtitle = '';

  if (!hasCategory) {
    title = `حوزه «${CATEGORY_LABELS[category]}» به شما اختصاص نیافته`;
    subtitle = 'اقدام شما ممکن است توسط سیستم رد شود. برای دسترسی کامل با مدیر ارشد تماس بگیرید.';
  } else if (isEligible) {
    const cur = shiftWindow.current;
    title = 'شما در شیفت هستید';
    subtitle = cur
      ? `شیفت فعلی: ${DAYS_OF_WEEK_FA[cur.dayOfWeek]}، ${formatMinuteFa(cur.startMinute)} تا ${formatMinuteFa(cur.endMinute)}`
      : 'می‌توانید اقدامات مربوط به این حوزه را انجام دهید.';
  } else {
    title = `خارج از شیفت — ${info.label}`;
    subtitle = shiftWindow.next
      ? `شیفت بعدی: ${DAYS_OF_WEEK_FA[shiftWindow.next.dayOfWeek]} ساعت ${formatMinuteFa(shiftWindow.next.startMinute)}${
          shiftWindow.next.daysAhead > 0 ? ` (${shiftWindow.next.daysAhead} روز بعد)` : ' (همین امروز)'
        }`
      : 'برنامه شیفت فعال برای شما تعریف نشده است.';
  }

  return (
    <div
      style={{
        direction: 'rtl',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '14px 16px',
        marginBottom: 16,
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderInlineStart: `4px solid ${s.iconColor}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 10,
          background: s.iconBg,
          color: s.iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 4,
        }}>
          <h4 style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 700,
            color: s.title,
            lineHeight: 1.4,
          }}>
            {title}
          </h4>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 10px',
            background: s.chipBg,
            color: s.chipText,
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.6,
          }}>
            <ScheduleIcon sx={{ fontSize: 12 }} />
            {info.label}
          </span>
        </div>
        <p style={{
          margin: 0,
          fontSize: 12.5,
          color: s.text,
          lineHeight: 1.7,
          opacity: 0.9,
        }}>
          {subtitle}
        </p>
      </div>
    </div>
  );
};

export default MyShiftStatusBanner;
